export default function useApi() {
  const config = useRuntimeConfig()
  const { getIdToken } = useAuth()

  async function apiGet<T>(
    endpoint: ApiGatewayUrl,
    { query, headers = {} }: GetFetchOptions,
    endpointContext?: EndpointContext,
  ): Promise<T> {
    let url = getLocalOrGatewayUrl(endpoint)

    if (endpointContext) {
      url = fillRouteTemplate(url, endpointContext)
    }

    return await $fetch<T>(url, {
      headers: { ...(await getBearerTokenHeader()), ...headers },
      query,
    })
  }

  async function cloudFunctionRequestApiPost<T>(
    functionRequestName: string,
    body: any,
    { query, headers = {} }: GetFetchOptions,
  ): Promise<T> {
    const url = `${config.public.CF_URL}/${functionRequestName}`

    return await $fetch<T>(url, {
      method: 'POST',
      headers: { ...(await getBearerTokenHeader()), ...headers },
      query,
      body,
    })
  }

  async function lspApiGet<T>(
    endpoint: lspApiEndpoint,
    { query, headers = {} }: GetFetchOptions,
    endpointContext?: EndpointContext,
  ): Promise<T> {
    const userJwt = await getLspApiToken()

    let url = `${config.public.LSP_URL}/${endpoint}`

    if (endpointContext) {
      url = fillRouteTemplate(url, endpointContext)
    }

    return await $fetch<T>(url, {
      headers: {
        Authorization: `Bearer ${userJwt}`,
        ...headers,
      },
      query,
    })
  }

  async function lspApiPost<T>(
    endpoint: lspApiEndpoint,
    body?: any,
    endpointContext?: EndpointContext,
    query?: any,
  ): Promise<T> {
    const userJwt = await getLspApiToken()

    let url = `${config.public.LSP_URL}/${endpoint}`

    if (endpointContext) {
      url = fillRouteTemplate(url, endpointContext)
    }

    return await $fetch<T>(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userJwt}`,
        'Content-Type': 'application/json',
      },
      query,
      body,
    })
  }

  async function getLspApiToken(): Promise<string> {
    const userJwt = await $fetch<{ token: string }>(
      '/Authorization-getUserJwt',
      {
        baseURL: config.public.CF_URL,
        headers: {
          Authorization: `Bearer ${await getIdToken()}`,
        },
      },
    )

    return userJwt.token
  }

  async function apiPost<T>(
    endpoint: ApiGatewayUrl,
    { query, body, headers = {} }: PostFetchOptions,
    endpointContext?: EndpointContext,
  ) {
    let url = getLocalOrGatewayUrl(endpoint)

    if (endpointContext) {
      url = fillRouteTemplate(url, endpointContext)
    }

    return await $fetch<T>(url, {
      method: 'POST',
      body: body,
      headers: { ...(await getBearerTokenHeader()), ...headers },
      query,
    })
  }

  /**
   * Make a post request to the API gateway: 'mais-internal-api'
   */
  async function apiPostNew<T>(
    endpoint: ApiGatewayUrl,
    { query, body, headers = {} }: PostFetchOptions,
    endpointContext?: EndpointContext,
  ) {
    let url: string = endpoint

    if (endpointContext) {
      url = fillRouteTemplate(url, endpointContext)
    }

    return await gatewayFetch<T>(url, {
      body,
      headers,
      query,
    })
  }

  async function getBearerTokenHeader(): Promise<{ Authorization?: string }> {
    const accessToken = await getIdToken()

    if (!accessToken) {
      return {}
    }

    return {
      Authorization: `Bearer ${accessToken}`,
    }
  }

  /**
   * When mais is deployed, we use a google cloud api gateway to get access to the back-end
   * When we develop locally, we do not run an api gateway (in between the front- and back-end) and call the cloud functions directly
   */
  function getLocalOrGatewayUrl(apiGatewayUrl: ApiGatewayUrl) {
    if (config.public.IS_USING_LOCAL_BACKEND) {
      // v2 domain events have prefix `Features-`
      if (apiGatewayUrl === 'v2-domain-events/{topic}/{event}') {
        return `${config.public.CF_GATEWAY_URL}/Features-${gatewayUrlToFunctionName[apiGatewayUrl]}`
      }

      return `${config.public.CF_URL}/Deliveries-${gatewayUrlToFunctionName[apiGatewayUrl]}`
    }

    return `${config.public.CF_GATEWAY_URL}/${apiGatewayUrl}`
  }

  function fillRouteTemplate(url: string, context: EndpointContext): string {
    return url.replace(/{(\w+)}/g, (_, key) => context[key] || '')
  }

  return {
    lspApiGet,
    lspApiPost,
    apiGet,
    apiPost,
    getLocalOrGatewayUrl,
    apiPostNew,
    cloudFunctionRequestApiPost,
    getBearerTokenHeader,
  }
}

export interface FetchOptionsBase {
  query?: Record<string, any>
  headers?: HeadersInit
}

export interface PostFetchOptions extends FetchOptionsBase {
  body?: any
}

export type GetFetchOptions = FetchOptionsBase

export interface EndpointContext {
  [key: string]: string
}

export type ApiGatewayUrl
  = | 'amp/create'
    | 'user/authenticate'
    | 'zoho/call-api'
    | 'amp/cancel'
    | 'bucket/download'
    | 'bucket/metadata'
    | 'bucket/upload'
    | 'notificare/call-api'
    | 'parallel-order/folders'
    | 'parallel-order/folder/files'
    | 'parallel-order/start-job'
    | 'amp/reprint'
    | 'shipping/{provider}/cancel/{shipmentId}'
    | 'shipping/{provider}/create'
    | 'shipping/{provider}/pickuppoint-active/{shipmentId}'
    | 'shipping/{provider}/reprint/{shipmentId}'
    | 'jspm/validate-licence'
    | 'checks/'
    | 'domain-event/{topic}/{event}'
    | 'accounts/'
    | 'standards/wcia'
    | 'v2-domain-events/{topic}/{event}'
    | 'medication-agreement/{medicationAgreementId}/dispense-type'
    | 'Authorization-getUserJwt'
    | 'authenticate-user'
    | 'domain-events/{topic}/{event}/'
    | 'authenticate-user'
    | 'domain-events/{topic}/{event}/'
    | 'support/mais-question'

export type lspApiEndpoint
  = | 'prescription/{lspPrescriptionId}'
    | 'zorg-id/session'
    | 'zorg-id/session/{sessionId}'
    | 'zorg-id/registration'
    | 'zorg-id/registration/delete'
    | 'zorg-id/registration/query'
    | 'dispense/query'

const gatewayUrlToFunctionName: Record<ApiGatewayUrl, string> = {
  'accounts/': 'httpsAccounts',
  'amp/create': 'httpsAMPCreateShipment',
  'user/authenticate': 'httpsAuthenticateUser',
  'zoho/call-api': 'httpsCallZohoApi',
  'amp/cancel': 'httpsCancelAMPLabel',
  'bucket/download': 'httpsGoogleCloudManager/download',
  'bucket/metadata': 'httpsGoogleCloudManager/metadata',
  'bucket/upload': 'httpsGoogleCloudManager/upload',
  'notificare/call-api': 'httpsNotificare',
  'parallel-order/folders':
    'httpsParallelOrderManager/getFoldersFromGoogleCloudBucket',
  'parallel-order/folder/files':
    'httpsParallelOrderManager/downloadFilesInFolderFromGoogleCloudBucket',
  'parallel-order/start-job': 'httpsParallelOrderManager/startParallelOrderJob',
  'amp/reprint': 'httpsReprintAMPLabel',
  'shipping/{provider}/cancel/{shipmentId}':
    'httpsShippingManagement/{provider}/cancel/{shipmentId}',
  'shipping/{provider}/create': 'httpsShippingManagement/{provider}/create',
  'shipping/{provider}/pickuppoint-active/{shipmentId}':
    'httpsShippingManagement/{provider}/pickuppoint-active/{shipmentId}',
  'shipping/{provider}/reprint/{shipmentId}':
    'httpsShippingManagement/{provider}/reprint/{shipmentId}',
  'jspm/validate-licence': 'httpsValidateJSPMLicense',
  'checks/': 'httpsPharmasistChecks/checks',
  'domain-event/{topic}/{event}':
    'httpsSendDomainEvent/domain-event/{topic}/{event}',
  'standards/wcia': 'httpsStandards/wcia',
  'v2-domain-events/{topic}/{event}':
    'httpsSendDomainEvent/v2-domain-events/{topic}/{event}',
  'medication-agreement/{medicationAgreementId}/dispense-type':
    'dispenseTypeHttpEntry/dispense-type/{medicationAgreementId}',
  'Authorization-getUserJwt': 'httpsAuthorization/getUserJwt',
  'authenticate-user': 'poc-authenticateUserHttpEntry',
  'domain-events/{topic}/{event}/': 'poc-employeeUserDomainEventEntry',
  'support/mais-question': 'support-submitMaisSupportQuestion',
}
